import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { useImpersonation } from '../../hooks/useImpersonation';
import { endImpersonation } from '../../Services/deviareAuth';
import {
  expireImpersonation,
  shouldNotify,
} from '../../Services/impersonationSession';
import { notify } from '../../utils';

const useStyles = makeStyles((theme) => ({
  bar: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2),
    background: '#6E2430',
    color: '#fff',
    boxSizing: 'border-box',
  },
  text: {
    minWidth: 0,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  note: {
    fontSize: 12,
    lineHeight: 1.4,
    opacity: 0.92,
  },
  button: {
    flexShrink: 0,
    color: '#6E2430',
    background: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': {
      background: '#f4f4f4',
    },
  },
}));

export function formatExpiry(seconds) {
  const total = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  }
  return `${minutes} min ${String(rest).padStart(2, '0')} sec`;
}

function remainingSeconds(snapshot) {
  if (!snapshot?.expiresAt) return snapshot?.expiresInSeconds || 0;
  return Math.max(0, Math.ceil((snapshot.expiresAt - Date.now()) / 1000));
}

const ImpersonationBanner = () => {
  const classes = useStyles();
  const snapshot = useImpersonation();
  const [remaining, setRemaining] = useState(() => remainingSeconds(snapshot));
  const [returning, setReturning] = useState(false);

  useEffect(() => {
    if (!snapshot.active) {
      setRemaining(0);
      return undefined;
    }
    const tick = () => {
      const next = remainingSeconds(snapshot);
      setRemaining(next);
      if (next <= 0 && expireImpersonation()) {
        const message = 'This sign-in session has expired. You are back on the superadmin account.';
        if (shouldNotify(message)) notify('info', message);
      }
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [snapshot]);

  useEffect(() => {
    const root = document.documentElement;
    if (!snapshot.active) {
      root.style.setProperty('--impersonation-banner-offset', '0px');
      document.body.style.paddingTop = '';
      return undefined;
    }
    const applyOffset = () => {
      const height = document.getElementById('impersonation-banner')?.offsetHeight || 52;
      const offset = `${height}px`;
      root.style.setProperty('--impersonation-banner-offset', offset);
      document.body.style.paddingTop = offset;
    };
    applyOffset();
    window.addEventListener('resize', applyOffset);
    return () => {
      window.removeEventListener('resize', applyOffset);
      root.style.setProperty('--impersonation-banner-offset', '0px');
      document.body.style.paddingTop = '';
    };
  }, [snapshot.active, remaining]);

  if (!snapshot.active) return null;

  const externalChanged = snapshot.keycloakSwitched || snapshot.externalPlatformsSwitched;
  const note = externalChanged
    ? 'An external session changed. Return to the superadmin account.'
    : 'Keycloak and learning sites stay on your superadmin session.';

  const handleReturn = async () => {
    setReturning(true);
    const result = await endImpersonation();
    setReturning(false);
    if (result.expired || result.alreadyEnded) return;
    if (result.ok) {
      const message = 'You are back on the superadmin account.';
      if (shouldNotify(message)) notify('success', message);
      return;
    }
    const message = result.message || 'Could not return to the superadmin account.';
    if (shouldNotify(message)) notify('error', message);
  };

  return (
    <div className={classes.bar} id="impersonation-banner" role="status">
      <div className={classes.text}>
        <div className={classes.title}>
          Signed in as {snapshot.targetName}. Expires in {formatExpiry(remaining)}.
        </div>
        <div className={classes.note}>{note}</div>
      </div>
      <Button
        className={classes.button}
        onClick={handleReturn}
        disabled={returning}
        variant="contained"
        disableElevation
      >
        {returning ? 'Returning…' : 'Return to superadmin'}
      </Button>
    </div>
  );
};

export default ImpersonationBanner;
